import { SLIDE_HEIGHT, SLIDE_WIDTH } from '../schemas/slides.js';
import { MONO_FONTS, SERIF_FONTS } from '../schemas/training.js';
import { mixColor, ROLE_FONT_SIZE, ROLE_LINE_HEIGHT } from './layout.js';
/** CSS font-family value for a design font, with a generic fallback. */
export function fontStack(font) {
    const serif = SERIF_FONTS.includes(font);
    const mono = MONO_FONTS.includes(font);
    return `'${font}', ${mono ? 'monospace' : serif ? 'Georgia, serif' : 'system-ui, sans-serif'}`;
}
/** The font, size, weight and color a text element's runs inherit from its role and the design. */
export function roleTextStyle(role, design) {
    const isTitle = role === 'title';
    return {
        fontFamily: fontStack(isTitle ? design.headingFont : design.bodyFont),
        fontSize: `${ROLE_FONT_SIZE[role]}px`,
        fontWeight: isTitle ? '700' : '400',
        color: isTitle ? design.primaryColor : design.textColor,
        lineHeight: String(ROLE_LINE_HEIGHT[role]),
    };
}
export function elementBoxStyle(el) {
    return {
        position: 'absolute',
        left: `${el.x}px`,
        top: `${el.y}px`,
        width: `${el.w}px`,
        height: `${el.h}px`,
        boxSizing: 'border-box',
        // Rotation is about the box's centre (the default transform-origin), as in PowerPoint.
        ...(el.rotation ? { transform: `rotate(${el.rotation}deg)` } : {}),
    };
}
export function textElementStyle(el, design) {
    return {
        ...elementBoxStyle(el),
        ...roleTextStyle(el.role, design),
        display: 'flex',
        flexDirection: 'column',
        // "safe": when the text is taller than its box it starts at the top and runs on downwards,
        // rather than spilling upwards out of a bottom- or centre-aligned box.
        justifyContent: el.verticalAlign === 'middle'
            ? 'safe center'
            : el.verticalAlign === 'bottom'
                ? 'safe flex-end'
                : 'flex-start',
        // Text that doesn't fit stays visible (as in PowerPoint), never silently cut off.
        overflow: 'visible',
        overflowWrap: 'break-word',
    };
}
export function paragraphStyle(p) {
    return {
        margin: '0',
        textAlign: p.align ?? 'left',
        position: 'relative',
        ...(p.bullet ? { paddingLeft: '1.1em', marginBottom: '0.35em' } : { marginBottom: '0.2em' }),
    };
}
export function bulletStyle(design) {
    return { position: 'absolute', left: '0', color: design.accentColor, fontWeight: '700' };
}
export function runStyle(run) {
    const style = {};
    // Explicit either way: titles default to bold, so un-bolding one has to render as normal weight.
    if (run.bold !== undefined)
        style.fontWeight = run.bold ? '700' : '400';
    if (run.italic)
        style.fontStyle = 'italic';
    if (run.underline)
        style.textDecoration = 'underline';
    if (run.color)
        style.color = run.color;
    if (run.fontSize)
        style.fontSize = `${run.fontSize}px`;
    if (run.fontFamily)
        style.fontFamily = fontStack(run.fontFamily);
    return style;
}
export function shapeStyle(el) {
    if (el.shape === 'line') {
        return {
            ...elementBoxStyle(el),
            height: '0px',
            top: `${el.y + el.h / 2}px`,
            borderTop: `${Math.max(el.strokeWidth, 2)}px solid ${el.stroke ?? el.fill ?? '#000000'}`,
        };
    }
    return {
        ...elementBoxStyle(el),
        background: el.fill ?? 'transparent',
        border: el.stroke && el.strokeWidth ? `${el.strokeWidth}px solid ${el.stroke}` : 'none',
        borderRadius: el.shape === 'ellipse' ? '50%' : el.shape === 'roundRect' ? '24px' : '0',
    };
}
/** Where a slide's number goes: the bottom-right corner, right-aligned, below the content. The
 * PPTX export puts PowerPoint's own slide-number field in the same box. */
export const SLIDE_NUMBER_BOX = { x: SLIDE_WIDTH - 120 - 240, y: SLIDE_HEIGHT - 84, w: 240, h: 40 };
export const SLIDE_NUMBER_FONT_SIZE = 24;
/** Text color, faded most of the way towards the background. */
export function slideNumberColor(design) {
    return mixColor(design.textColor, design.backgroundColor, 0.45);
}
export function slideNumberStyle(design) {
    return {
        position: 'absolute',
        left: `${SLIDE_NUMBER_BOX.x}px`,
        top: `${SLIDE_NUMBER_BOX.y}px`,
        width: `${SLIDE_NUMBER_BOX.w}px`,
        height: `${SLIDE_NUMBER_BOX.h}px`,
        textAlign: 'right',
        fontFamily: fontStack(design.bodyFont),
        fontSize: `${SLIDE_NUMBER_FONT_SIZE}px`,
        lineHeight: `${SLIDE_NUMBER_BOX.h}px`,
        color: slideNumberColor(design),
        letterSpacing: '0.04em',
    };
}
export function placeholderStyle(design) {
    return {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '32px',
        boxSizing: 'border-box',
        border: `4px dashed ${design.accentColor}`,
        borderRadius: '16px',
        color: design.textColor,
        opacity: '0.7',
        fontFamily: fontStack(design.bodyFont),
        fontSize: '28px',
        fontStyle: 'italic',
    };
}
/** SVG as an <img> source. Loading it as an image rather than inlining it means scripts and
 * external references in the markup never run — Gemini wrote it, so it's treated as untrusted. */
export function svgDataUri(svg) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}
export function toCssText(style) {
    return Object.entries(style)
        .map(([key, value]) => `${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}:${value}`)
        .join(';');
}
export function escapeHtml(text) {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
function attr(style) {
    return escapeHtml(toCssText(style));
}
function renderParagraph(p, design) {
    const runs = p.runs.some((r) => r.text)
        ? p.runs.map((r) => `<span style="${attr(runStyle(r))}">${escapeHtml(r.text)}</span>`).join('')
        : '<br>';
    const bullet = p.bullet ? `<span style="${attr(bulletStyle(design))}">•</span>` : '';
    return `<div style="${attr(paragraphStyle(p))}">${bullet}${runs}</div>`;
}
function renderImage(el, design, opts, cls) {
    const src = el.storagePath ? opts.resolveImage(el.storagePath) : null;
    const inner = src
        ? `<img src="${escapeHtml(src)}" alt="" style="width:100%;height:100%;object-fit:${el.fit};display:block">`
        : `<div style="${attr(placeholderStyle(design))}">${escapeHtml(el.placeholder ?? 'Image')}</div>`;
    return `<div ${cls} style="${attr(elementBoxStyle(el))}">${inner}</div>`;
}
export function renderElementHtml(el, design, opts) {
    // The pulse-el-* class lets a page animate elements by kind (the presenter's entrance).
    const cls = `class="pulse-el pulse-el-${el.type}"`;
    switch (el.type) {
        case 'text':
            return `<div ${cls} style="${attr(textElementStyle(el, design))}">${el.paragraphs.map((p) => renderParagraph(p, design)).join('')}</div>`;
        case 'image':
            return renderImage(el, design, opts, cls);
        case 'diagram':
            return `<div ${cls} style="${attr(elementBoxStyle(el))}"><img src="${escapeHtml(svgDataUri(el.svg))}" alt="${escapeHtml(el.description)}" style="width:100%;height:100%;object-fit:contain;display:block"></div>`;
        case 'shape':
            return `<div ${cls} style="${attr(shapeStyle(el))}"></div>`;
    }
}
export function slideStyle(slide) {
    return {
        position: 'relative',
        width: `${SLIDE_WIDTH}px`,
        height: `${SLIDE_HEIGHT}px`,
        background: slide.background,
        overflow: 'hidden',
    };
}
/** One slide as a self-contained, fixed-size HTML fragment (no fonts — see googleFontsHref). */
export function renderSlideHtml(slide, design, opts) {
    const number = slide.showNumber && opts.slideNumber
        ? `<div class="pulse-slide-number" style="${attr(slideNumberStyle(design))}">${opts.slideNumber}</div>`
        : '';
    return `<div class="pulse-slide" style="${attr(slideStyle(slide))}">${slide.elements
        .map((el) => renderElementHtml(el, design, opts))
        .join('')}${number}</div>`;
}
/** Every font a deck uses: its design's two plus any set on individual runs. */
export function fontsUsedInDeck(deck) {
    const fonts = new Set([deck.design.headingFont, deck.design.bodyFont]);
    for (const slide of deck.slides)
        for (const el of slide.elements)
            if (el.type === 'text')
                for (const p of el.paragraphs)
                    for (const r of p.runs)
                        if (r.fontFamily)
                            fonts.add(r.fontFamily);
    return [...fonts];
}
/** Google Fonts stylesheet URL for the given fonts, regular and bold. */
export function googleFontsHref(fonts) {
    const families = fonts.map((f) => `family=${encodeURIComponent(f).replace(/%20/g, '+')}:wght@400;700`);
    return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}
/**
 * A whole printable document: one slide per page at exactly SLIDE_WIDTH x SLIDE_HEIGHT. This is
 * what the export service hands to Chromium for PDF.
 */
export function renderDeckDocument(decks, opts) {
    const fonts = [...new Set(decks.flatMap(fontsUsedInDeck))];
    // Slides are numbered through the whole document, as PowerPoint numbers the PPTX export.
    const pages = decks
        .flatMap((deck) => deck.slides.map((slide) => ({ slide, design: deck.design })))
        .map(({ slide, design }, i) => renderSlideHtml(slide, design, { ...opts, slideNumber: i + 1 }))
        .map((html) => `<section class="page">${html}</section>`)
        .join('');
    return [
        '<!doctype html><html><head><meta charset="utf-8">',
        `<title>${escapeHtml(opts.title)}</title>`,
        `<link rel="stylesheet" href="${escapeHtml(googleFontsHref(fonts))}">`,
        '<style>',
        `@page { size: ${SLIDE_WIDTH}px ${SLIDE_HEIGHT}px; margin: 0; }`,
        'html, body { margin: 0; padding: 0; }',
        `.page { width: ${SLIDE_WIDTH}px; height: ${SLIDE_HEIGHT}px; overflow: hidden; break-after: page; }`,
        '.page:last-child { break-after: auto; }',
        '* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }',
        '</style></head><body>',
        pages,
        '</body></html>',
    ].join('');
}
//# sourceMappingURL=render.js.map